// components/UserAvatar.tsx
import React, { useState, useRef, useCallback, useEffect, memo, useMemo } from 'react';
import { Camera, Crown } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import ImageCropModal, { CropResult } from './ImageCropModal';
import { toast } from 'sonner';

interface UserAvatarProps {
  userId: string;
  name: string;
  canEdit: boolean;
  isOrganisationAdmin?: boolean | null;
  size?: number | string;
  className?: string;
  shape?: 'circle' | 'square';
  onAvatarUpdated?: () => void;
  externalTimestamp?: number;
}

const MemoizedCamera = memo(Camera);
const MemoizedCrown = memo(Crown);

const INHERIT_RADIUS_STYLE: React.CSSProperties = { borderRadius: 'inherit' };
const CROP_OUTPUT_SIZE = { width: 386, height: 386 };
const INITIALS_BASE_STYLE: React.CSSProperties = {
  fontFamily: 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  lineHeight: 1,
};

const UserAvatar = memo(({ 
  userId, 
  name, 
  canEdit, 
  isOrganisationAdmin,
  size, 
  className = "",
  shape = 'circle',
  onAvatarUpdated ,
  externalTimestamp
}: UserAvatarProps) => {
  const { kernel } = useKernel();
  const [imgError, setImgError] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [timestamp, setTimestamp] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeTimestamp = useMemo(() => Math.max(externalTimestamp || 0, timestamp), [externalTimestamp, timestamp]);
  const avatarUrl = useMemo(() => {
    const base = `/api/user/avatar?userId=${userId}`;
    return activeTimestamp > 0 ? `${base}&t=${activeTimestamp}` : base;
  }, [userId, activeTimestamp]);

  const radiusClass = useMemo(() => shape === 'circle' ? 'rounded-full' : '', [shape]);
  const initials = useMemo(() => {
    const source = (name || 'User').trim();
    if (!source) return 'U';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      const first = parts[0]!;
      return first.slice(0, 2);
    }
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`;
  }, [name]);
  
  const containerStyle = useMemo((): React.CSSProperties => size 
    ? { width: size, height: size } 
    : { width: '100%', height: '100%' }, [size]);

  const iconSizes = useMemo(() => {
    return {
      camera: typeof size === 'number' ? size * 0.3 : (typeof size === 'string' ? `calc(${size} * 0.3)` : '30%'),
      crown: typeof size === 'number' ? size * 0.25 : (typeof size === 'string' ? `calc(${size} * 0.25)` : 20)
    };
  }, [size]);
  const initialsFontSize = useMemo(() => {
    if (typeof size === 'number') {
      return `${Math.max(10, Math.round(size * 0.42))}px`;
    }
    if (typeof size === 'string') {
      return `calc(${size} * 0.42)`;
    }
    return '42%';
  }, [size]);
  const initialsStyle = useMemo((): React.CSSProperties => ({ ...INITIALS_BASE_STYLE, fontSize: initialsFontSize }), [initialsFontSize]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setIsCropModalOpen(true);
      e.target.value = '';
    }
  }, []);

  const handleCropConfirm = useCallback(async (result: CropResult) => {
    if (!result.file) return;

    const toastId = toast.loading('Uploading avatar...');

    try {
      await kernel.current?.uploadOrganisationMemberAvatar(userId, result.file);

      toast.success('Avatar updated', { id: toastId });
      setIsCropModalOpen(false);
      setImgError(false);
      setTimestamp(Date.now());
      if (onAvatarUpdated) onAvatarUpdated();
      
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : 'Upload failed';
      toast.error(`Failed to upload: ${message}`, { id: toastId });
    }
  }, [kernel, userId, onAvatarUpdated]);

  const handleImgError = useCallback(() => setImgError(true), []);
  const handleEditClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  }, []);
  const handleModalClose = useCallback(() => setIsCropModalOpen(false), []);

  useEffect(() => {
    setImgError(false);
  }, [activeTimestamp]);

  const avatarContent = (
    <>
      {!imgError ? (
        <img
          src={avatarUrl}
          alt={`${name}'s avatar`}
          className="h-full w-full object-cover"
          onError={handleImgError}
          loading="lazy"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-white/45"
          style={initialsStyle}
        >
          {initials}
        </div>
      )}

      {canEdit ? (
        <span
          className="absolute inset-0 z-10 flex items-center justify-center bg-white/[0.14] opacity-0 backdrop-blur-[1px] transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden="true"
        >
          <MemoizedCamera size={iconSizes.camera} className="text-white/90" />
        </span>
      ) : null}
    </>
  );

  return (
    <div
      className={`relative flex-shrink-0 select-none bg-white/[0.04] ${radiusClass} ${className}`}
      style={containerStyle}
    >
      {canEdit ? (
        <button
          type="button"
          className="group relative h-full w-full cursor-pointer overflow-hidden outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#3B82F6]"
          style={INHERIT_RADIUS_STYLE}
          onClick={handleEditClick}
          aria-label={`Change avatar for ${name}`}
        >
          {avatarContent}
        </button>
      ) : (
        <div className="relative h-full w-full overflow-hidden" style={INHERIT_RADIUS_STYLE}>
          {avatarContent}
        </div>
      )}

      {isOrganisationAdmin && (
        <div className="absolute right-0 top-0 z-20 p-1" title="Organisation admin">
          <MemoizedCrown size={iconSizes.crown} className="fill-current text-[#FFDC49]/80" />
        </div>
      )}

      <input 
        type="file" 
        ref={fileInputRef}
        className="hidden"
        accept="image/png, image/jpeg, image/webp"
        onChange={handleFileSelect}
      />

      {isCropModalOpen && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          imageFile={selectedFile}
          initialPolygon={null}
          imageIndex={0}
          onClose={handleModalClose}
          onConfirm={handleCropConfirm}
          lang="en"
          instruction="Center the face for your avatar"
          forceOutputSize={CROP_OUTPUT_SIZE}
          forceOutputFormat="image/webp"
        />
      )}
    </div>
  );
});

UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
